/**
 * Hook for registering module auth-transition handlers
 *
 * This hook observes `authStore$.isAuthenticated` from module-auth.
 * - When authentication transitions `true` → `false` (logout / session expiry),
 *   it clears all cart-related stores via quoteLogoutHandler.
 * - When authentication transitions `false` → `true` (login / registration),
 *   it resets the customerCartLoaded flag so the cart provider's auth effect
 *   re-runs and fetches/merges the customer cart.
 *
 * This keeps the dependency direction correct:
 *   module-quote → observes → module-auth (never the reverse).
 *
 * USAGE:
 * Place this hook in a component that is always mounted when the module is active,
 * typically in the module's main provider or navigator.
 *
 * ```typescript
 * // In QuoteProvider or CartProvider
 * export const QuoteProvider = ({children}) => {
 *     useQuoteModuleLogout();
 *     return <>{children}</>;
 * };
 * ```
 *
 * @see mobile: module-quote/src/hooks/use-quote-module-logout.ts
 */

'use client';

import { useEffect, useRef } from 'react';
import { authStore$ } from '@kidslabs/module-auth';
import { cartStoreAPI, cartItemReferenceStoreAPI } from '../stores';
import { cartSessionManager } from '../contracts';
import { setInternalUpdate, updateLastSyncedState } from '../stores/cart-item-reference.store';
import { storeCodeEmitter } from '@kidslabs/module-core';
import { cartTransientStore$ } from '../stores/cart.store';

/**
 * Logout handler for quote module
 * Clears all cart-related stores AND session cart IDs
 */
const quoteLogoutHandler = async (): Promise<void> => {
    console.log('[QuoteModule] Executing logout handler - clearing cart stores and session');

    try {
        // Clear main cart store (also resets customerCartLoaded + cartInitialized)
        cartStoreAPI.actions.resetCart();

        // Clear cart item reference store (wrapped to prevent spurious sync)
        setInternalUpdate(true);
        try {
            cartItemReferenceStoreAPI.actions.resetStore();
            updateLastSyncedState([]);
        } finally {
            setInternalUpdate(false);
        }

        // Clear session cart ID (Redis) so the visitor session
        // does not retain the customer's cart after logout
        if (cartSessionManager.hasAdapter()) {
            const storeCode = storeCodeEmitter.getCurrentStoreCode();
            if (storeCode) {
                await cartSessionManager.clearCartId(storeCode);
            }
        }

        console.log('[QuoteModule] All cart stores and session cleared successfully');
    } catch (error) {
        console.error('[QuoteModule] Error clearing cart stores during logout:', error);
        throw error;
    }
};

/**
 * Login handler for quote module
 * Resets transient cart flags so the cart provider's auth effect
 * re-runs to fetch and merge the customer cart.
 */
const quoteLoginHandler = (): void => {
    console.log('[QuoteModule] Executing login handler - resetting cart lifecycle flags');
    cartTransientStore$.customerCartLoaded.set(false);
    cartTransientStore$.cartInitialized.set(false);
};

/**
 * Hook to observe auth state and execute handlers on auth transitions
 *
 * Listens to `authStore$.isAuthenticated` changes.
 * - `true` → `false`: calls `quoteLogoutHandler()` (logout / session expiry)
 * - `false` → `true`: calls `quoteLoginHandler()` (login / registration)
 *
 * This covers:
 * - Manual logout (user clicks logout)
 * - Session expiry (NextAuth RefreshAccessTokenError → signOut)
 * - Successful login or registration
 * - Any other scenario where auth state changes
 */
export const useQuoteModuleLogout = (): void => {
    const previousAuthStateRef = useRef<boolean>(authStore$.isAuthenticated.peek() ?? false);

    useEffect(() => {
        // Subscribe to authStore$.isAuthenticated changes
        const unsubscribe = authStore$.isAuthenticated.onChange(({ value }) => {
            const currentAuthState = value ?? false;
            const previousAuthState = previousAuthStateRef.current;

            // Detect logout transition: true → false
            if (previousAuthState && !currentAuthState) {
                console.log('[QuoteModule] Auth transition detected: logged-in → logged-out. Clearing cart stores.');
                quoteLogoutHandler();
            }

            // Detect login transition: false → true
            if (!previousAuthState && currentAuthState) {
                console.log(
                    '[QuoteModule] Auth transition detected: logged-out → logged-in. Resetting cart lifecycle flags.'
                );
                quoteLoginHandler();
            }

            previousAuthStateRef.current = currentAuthState;
        });

        console.log('[QuoteModule] Auth state observer registered for auth transition detection');

        // Cleanup subscription on unmount
        return () => {
            unsubscribe();
            console.log('[QuoteModule] Auth state observer unregistered');
        };
    }, []);
};

// Export the handlers for direct usage if needed
export { quoteLogoutHandler, quoteLoginHandler };
